// hooks/useDraftInvoice.ts
import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useState } from 'react';
import { useRef } from 'react';
import {
  fetchDraftInvoicesList,
  fetchDraftInvoicesWithParams,
  setCurrentPage,
  setPageSize,
  clearError,
  clearMessage,
  clearInvoicesData,
} from '../features/draft-invoice/draft-invoice.slice';
import type { RootState, AppDispatch } from '../store';
import { DraftInvoiceParams } from '../api/draft-invoice/index';

const sanitizeParams = (params: Partial<DraftInvoiceParams>): Partial<DraftInvoiceParams> => {
  return {
    ...(params.PageNumber && { PageNumber: Math.max(1, Math.floor(Number(params.PageNumber))) }),
    ...(params.PageSize && { PageSize: Math.max(1, Math.floor(Number(params.PageSize))) }),
  };
};

export const useDraftInvoice = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const lastRequestRef = useRef<{ key: string; time: number } | null>(null);

  const {
    invoicesData,
    loading,
    error,
    message,
    pagination,
    filters,
  } = useSelector((state: RootState) => state.draftInvoice);

  const fetchDraftInvoices = useCallback(() => {
    const requestParams: DraftInvoiceParams = {
      PageNumber: pagination.currentPage,
      PageSize: pagination.pageSize,
      ...(searchQuery && { searchQuery }),
    };

    // Sanitize parameters before sending to API
    const sanitizedParams = sanitizeParams(requestParams);
    const finalParams: DraftInvoiceParams = {
      PageNumber: sanitizedParams.PageNumber || 1,
      PageSize: sanitizedParams.PageSize || 5,
      ...(searchQuery && { searchQuery }),
    };
    try {
      const key = JSON.stringify(finalParams);
      const now = Date.now();
      const last = lastRequestRef.current;
      if (last && last.key === key && now - last.time < 700) {
        return;
      }
      lastRequestRef.current = { key, time: now };
    } catch (e) {
    }

    return dispatch(fetchDraftInvoicesList(finalParams));
  }, [dispatch, pagination.currentPage, pagination.pageSize, filters.type, filters.status, searchQuery]);

  const fetchDraftInvoicesWithCustomParams = useCallback((params: Record<string, any>) => {
    return dispatch(fetchDraftInvoicesWithParams(params));
  }, [dispatch]);

  const handleSetCurrentPage = useCallback((page: number) => {
    dispatch(setCurrentPage(page));
  }, [dispatch]);

  const handleSetPageSize = useCallback((size: number) => {
    dispatch(setPageSize(size));
  }, [dispatch]);

  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
    handleSetCurrentPage(1);
  }, [handleSetCurrentPage]);

  // Clear error
  const handleClearError = useCallback(() => {
    dispatch(clearError());
  }, [dispatch]);

  // Clear message
  const handleClearMessage = useCallback(() => {
    dispatch(clearMessage());
  }, [dispatch]);

  // Clear all invoice data
  const handleClearInvoicesData = useCallback(() => {
    dispatch(clearInvoicesData());
  }, [dispatch]);

  // Auto-fetch data when pagination or filters change
  const autoFetch = useCallback((enabled: boolean = true) => {
    if (enabled) {
      fetchDraftInvoices();
    }
  }, [fetchDraftInvoices]);

  // Refresh data
  const refreshData = useCallback(() => {
    return fetchDraftInvoices();
  }, [fetchDraftInvoices]);

  // Go to next page
  const goToNextPage = useCallback(() => {
    if (pagination.hasNextPage) {
      handleSetCurrentPage(pagination.currentPage + 1);
    }
  }, [pagination.hasNextPage, pagination.currentPage, handleSetCurrentPage]);

  // Go to previous page
  const goToPreviousPage = useCallback(() => {
    if (pagination.hasPreviousPage) {
      handleSetCurrentPage(pagination.currentPage - 1);
    }
  }, [pagination.hasPreviousPage, pagination.currentPage, handleSetCurrentPage]);

  const goToFirstPage = useCallback(() => {
    handleSetCurrentPage(1);
  }, [handleSetCurrentPage]);

  const goToLastPage = useCallback(() => {
    if (pagination.totalPages > 0) {
      handleSetCurrentPage(pagination.totalPages);
    }
  }, [pagination.totalPages, handleSetCurrentPage]);

  const resetToDefaults = useCallback(() => {
    handleSetCurrentPage(1);
    handleSetPageSize(5);
    handleClearError();
    handleClearMessage();
    setSearchQuery('');
  }, [handleSetCurrentPage, handleSetPageSize, handleClearError, handleClearMessage]);
  
  return {
    invoicesData,
    loading,
    error,
    message,
    pagination,
    filters,
    searchQuery,

    fetchDraftInvoices,
    fetchDraftInvoicesWithCustomParams,
    setCurrentPage: handleSetCurrentPage,
    setPageSize: handleSetPageSize,
    handleSearch,
    clearError: handleClearError,
    clearMessage: handleClearMessage,
    clearInvoicesData: handleClearInvoicesData,

    autoFetch,
    refreshData,
    goToNextPage,
    goToPreviousPage,
    goToFirstPage,
    goToLastPage,
    resetToDefaults,
  };
};
